import React from 'react';
import { Phone, Pencil, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';

const STATUS_BADGE = { active: 'badge-info', repaid: 'badge-success', overdue: 'badge-danger' };

export const LoansTable = ({ loans, loading, canEdit, onRepay, onEdit, onDelete, onSchedule }) => (
  <Card data-testid="loans-table">
    <CardHeader>
      <CardTitle className="font-heading">Active Loans</CardTitle>
      <CardDescription>All loan accounts and their repayment status</CardDescription>
    </CardHeader>
    <CardContent className="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Borrower</TableHead>
            <TableHead>Business</TableHead>
            <TableHead>Loan Amount</TableHead>
            <TableHead>Interest</TableHead>
            <TableHead>Remaining</TableHead>
            <TableHead>Security</TableHead>
            <TableHead className="text-right">Savings</TableHead>
            <TableHead>Guarantor</TableHead>
            <TableHead>Status</TableHead>
            {canEdit && <TableHead></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-8">Loading...</TableCell>
            </TableRow>
          ) : loans.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                No loans found
              </TableCell>
            </TableRow>
          ) : (
            loans.map((loan) => (
              <TableRow key={loan.id} className="table-row-hover">
                <TableCell>
                  <div>
                    <p className="font-medium">{loan.borrower_name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {loan.borrower_phone}
                    </p>
                  </div>
                </TableCell>
                <TableCell>{loan.business_type}</TableCell>
                <TableCell className="font-medium">{formatCurrency(loan.loan_amount)}</TableCell>
                <TableCell>{loan.interest_rate}%</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{formatCurrency(loan.remaining_balance)}</p>
                    <Progress
                      value={100 - (loan.remaining_balance / loan.total_repayment * 100)}
                      className="w-20 h-1.5 mt-1"
                    />
                  </div>
                </TableCell>
                <TableCell className="text-sm max-w-xs truncate" title={loan.security}>{loan.security || '—'}</TableCell>
                <TableCell className="text-right">{loan.savings ? formatCurrency(loan.savings) : '—'}</TableCell>
                <TableCell className="text-sm">
                  {loan.guarantor_name ? (
                    <div>
                      <p className="font-medium">{loan.guarantor_name}</p>
                      {loan.guarantor_relationship && <p className="text-xs text-muted-foreground">{loan.guarantor_relationship}</p>}
                      {loan.guarantor_contact && <p className="text-xs text-muted-foreground">{loan.guarantor_contact}</p>}
                      {loan.guarantor_address && <p className="text-xs text-muted-foreground truncate max-w-[150px]">{loan.guarantor_address}</p>}
                    </div>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Badge className={STATUS_BADGE[loan.status] || ''}>
                    {loan.status}
                  </Badge>
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex flex-col gap-1.5">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onRepay(loan)}
                        disabled={loan.status === 'repaid'}
                        data-testid={`record-repayment-${loan.id}`}
                      >
                        Record Repayment
                      </Button>
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onEdit(loan)}
                          data-testid={`edit-loan-${loan.id}`}
                          className="flex-1 h-7 px-2 text-xs"
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDelete(loan)}
                          data-testid={`delete-loan-${loan.id}`}
                          className="flex-1 h-7 px-2 text-xs border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onSchedule(loan)}
                        data-testid={`view-schedule-${loan.id}`}
                        className="h-7 text-xs"
                      >
                        View Schedule
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);
